/**
 * Chip-based filter builder — no query language. Operators adapt to field type.
 * Edits spec.filters. Server-applies on indexed columns; falls back gracefully.
 */
import { useState } from 'react'
import { nanoid } from 'nanoid'
import { Filter, X, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover, PopoverTrigger, PopoverContent,
} from '@/components/ui/popover'
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select'
import { isServerQueryable, type FormCatalog, type CatalogField } from '../lib/column-catalog'
import type { FilterOp, ViewFilter } from '../types/data-view-spec'
import type { FieldType } from '@/features/form-builder/api/form-builder-types'

export const OP_LABELS: Record<FilterOp, string> = {
  eq: 'برابر', neq: 'نابرابر', contains: 'شامل',
  num_gt: 'بزرگتر از', num_gte: 'بزرگتر/مساوی', num_lt: 'کوچکتر از', num_lte: 'کوچکتر/مساوی',
  before: 'قبل از', after: 'بعد از',
  is_empty: 'خالی', is_not_empty: 'پر', has_option: 'دارای گزینه',
}

export function opsForType(t: FieldType): FilterOp[] {
  switch (t) {
    case 'number': return ['eq', 'neq', 'num_gt', 'num_gte', 'num_lt', 'num_lte', 'is_empty', 'is_not_empty']
    case 'date': case 'datetime': case 'time': return ['eq', 'after', 'before', 'is_empty', 'is_not_empty']
    case 'single_select': return ['eq', 'neq', 'is_empty', 'is_not_empty']
    case 'multi_select': return ['has_option', 'is_empty', 'is_not_empty']
    case 'boolean': return ['eq']
    default: return ['contains', 'eq', 'neq', 'is_empty', 'is_not_empty']
  }
}

export const NO_VALUE: FilterOp[] = ['is_empty', 'is_not_empty']

export function displayFilterValue(field: CatalogField | undefined, value?: string): string {
  if (value === undefined) return ''
  if (field?.options?.[value]) return field.options[value]
  if (field?.fieldType === 'boolean') return value === 'true' ? 'بله' : 'خیر'
  return value
}

interface FilterBarProps {
  catalog: FormCatalog
  filters: ViewFilter[]
  onChange: (f: ViewFilter[]) => void
}

export function FilterBar({ catalog, filters, onChange }: FilterBarProps) {
  const queryable = catalog.fields.filter(isServerQueryable)

  const remove = (id: string) => onChange(filters.filter((f) => f.id !== id))

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {filters.map((f) => {
        const field = catalog.byKey[f.key]
        return (
          <span key={f.id} className="inline-flex items-center gap-1.5 ps-2.5 pe-1 py-1 rounded-full bg-muted text-xs">
            <span className="font-medium">{field?.label ?? f.key}</span>
            <span className="text-muted-foreground">{OP_LABELS[f.op]}</span>
            {!NO_VALUE.includes(f.op) && <span className="text-foreground">{displayFilterValue(field, f.value)}</span>}
            <button onClick={() => remove(f.id)} className="text-muted-foreground hover:text-foreground"><X className="w-3 h-3" /></button>
          </span>
        )
      })}

      <AddFilter queryable={queryable} onAdd={(f) => onChange([...filters, f])} />
    </div>
  )
}

function AddFilter({ queryable, onAdd }: { queryable: CatalogField[]; onAdd: (f: ViewFilter) => void }) {
  const [open, setOpen] = useState(false)
  const [key, setKey] = useState<string>('')
  const [op, setOp] = useState<FilterOp>('contains')
  const [value, setValue] = useState('')

  const field = queryable.find((f) => f.fieldKey === key)
  const ops = field ? opsForType(field.fieldType) : ['contains' as FilterOp]
  const needsValue = !NO_VALUE.includes(op)

  const reset = () => { setKey(''); setOp('contains'); setValue('') }

  const submit = () => {
    if (!key) return
    onAdd({ id: nanoid(6), key, op, value: needsValue ? value : undefined })
    reset()
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset() }}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5 h-7">
          <Filter className="w-3.5 h-3.5" />افزودن فیلتر
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 space-y-2">
        <Select value={key} onValueChange={(v) => { setKey(v); const fl = queryable.find((f) => f.fieldKey === v); setOp(fl ? opsForType(fl.fieldType)[0] : 'contains') }}>
          <SelectTrigger className="h-8"><SelectValue placeholder="انتخاب فیلد" /></SelectTrigger>
          <SelectContent>
            {queryable.map((f) => <SelectItem key={f.fieldKey} value={f.fieldKey}>{f.label}</SelectItem>)}
          </SelectContent>
        </Select>

        {field && (
          <Select value={op} onValueChange={(v) => setOp(v as FilterOp)}>
            <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ops.map((o) => <SelectItem key={o} value={o}>{OP_LABELS[o]}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        {field && needsValue && (
          field.fieldType === 'boolean' ? (
            <Select value={value} onValueChange={setValue}>
              <SelectTrigger className="h-8"><SelectValue placeholder="مقدار" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="true">بله</SelectItem>
                <SelectItem value="false">خیر</SelectItem>
              </SelectContent>
            </Select>
          ) : field.options ? (
            <Select value={value} onValueChange={setValue}>
              <SelectTrigger className="h-8"><SelectValue placeholder="گزینه" /></SelectTrigger>
              <SelectContent>
                {Object.entries(field.options).map(([k, lbl]) => <SelectItem key={k} value={k}>{lbl}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Input
              className="h-8"
              type={field.fieldType === 'number' ? 'number' : 'text'}
              placeholder="مقدار"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
            />
          )
        )}

        <Button size="sm" className="w-full gap-1.5" disabled={!key || (needsValue && !value)} onClick={submit}>
          <Plus className="w-3.5 h-3.5" />اعمال
        </Button>
      </PopoverContent>
    </Popover>
  )
}
